import { getHoldingDetailsColumns } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/holdingEventsLogTableColumns.tsx';
import { BasicMantineTable } from '@dealstack/frontend/shared/components/BasicMantineTable/BasicMantineTable.tsx';
import React from 'react';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { mapBlocksToText, mapBlockToText } from '@dealstack/shared/lib/format/block.ts';
import { Divider, Flex, Paper, Text } from '@mantine/core';
import { HoldingEventDto } from '@dealstack/shared/lib/generated';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	CombinedEventDto,
	isHoldingEventDto,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/types.ts';

export const HoldingEventsLogTable = ({
	holdingEventsData,
}: {
	holdingEventsData?: CombinedEventDto[];
}) => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();

	return (
		<BasicMantineTable<CombinedEventDto>
			columns={getHoldingDetailsColumns({
				tCapManage,
				tCommonUi,
			})}
			isLoading={false}
			data={holdingEventsData ?? []}
			hideTopToolbar
			enableExpanding={true}
			enableColumnResizing={true}
			mantineTableBodyRowProps={({ row }) => ({
				id: row.id,
				onClick: () => {
					row.toggleExpanded();
				},
			})}
			renderDetailPanel={({ row, table }) => {
				const meta = table.options.meta as {
					gridLayout?: {
						getGridColumnForColumn: (columnId: string) => number;
						gridColumnCount: number;
						gridTemplateColumns: string;
					};
				};

				const gridLayout = meta?.gridLayout;
				const descriptionColumnStart =
					gridLayout?.getGridColumnForColumn('description') ?? 1;
				const gridTemplateColumns = gridLayout?.gridTemplateColumns ?? 'auto';

				if (isHoldingEventDto(row.original)) {
					return (
						<div
							style={{
								display: 'grid',
								gridTemplateColumns,
								width: '100%',
							}}
						>
							<div
								style={{
									gridColumn: `${descriptionColumnStart} / -1`,
									paddingLeft: '0.5rem',
								}}
							>
								<HoldingExpandedRowContent row={row.original} />
							</div>
						</div>
					);
				}
				return null;
			}}
			mantineDetailPanelProps={{
				sx: (theme) => ({
					background: theme.colors.gray[0],
					paddingTop: '1rem',
					paddingBottom: '1rem',
					fontSize: theme.fontSizes.sm,
				}),
			}}
		/>
	);
};

const HoldingExpandedRowContent = ({ row }: { row: HoldingEventDto }) => {
	const tCommonUi = useTranslationCommonUi();
	const blocksRanges = mapBlocksToText(row.blocksMoving, row.securityUnitDenominator);

	return (
		<Flex direction="column" gap="0.5rem">
			<Flex gap="0.5rem" align="flex-start">
				<Flex direction="column" gap="0.25rem">
					{!!blocksRanges.length && (
						<Text variant="p-md">
							{tCommonUi('block')} {blocksRanges}
						</Text>
					)}
				</Flex>
			</Flex>
			<div>
				{row.blocksMoving.map((block, index) => (
					<Flex key={`block-${index}`} align="center" gap="0.25rem" mt="0.25rem">
						<Text variant="B6Medium" fw={600}>
							{row.sourceDisplayName}
						</Text>
						<Flex align="center" sx={(theme) => ({ color: theme.colors.dark[8] })}>
							<Divider
								w="14px"
								sx={(theme) => ({ borderColor: theme.colors.dark[8] })}
							/>
							<Paper
								withBorder
								p="0.5rem"
								radius="sm"
								bg="transparent"
								sx={(theme) => ({
									borderColor: `${theme.colors.dark[8]} !important`,
								})}
							>
								<Text variant="p-md">
									{mapBlockToText(block, row.securityUnitDenominator)}
								</Text>
							</Paper>
							<Icon variant="arrow-right-v3" size="lg" />
						</Flex>
						<Text variant="B6Medium" fw={600}>
							{row.destDisplayName}
						</Text>
					</Flex>
				))}
			</div>
		</Flex>
	);
};
